/**
 * Service Builder cost arithmetic, pure and testable.
 *
 * THE PROGRAM-RULES ASSUMPTION THIS ENCODES (owner question, 2026-08-25; recorded in the
 * legislation register): a Support at Home participant can buy services BEYOND their funded
 * package, privately, at FULL price. The government subsidy and the category contribution rates
 * (clinical 0%, independence %, everyday living %) apply only to services WITHIN the budget;
 * above it there is no subsidy and no contribution framework — the household simply pays the
 * provider's price. Before this split, the builder charged the contribution rate on EVERYTHING
 * selected, so an over-budget plan looked almost free ($59 of contribution on a plan $21k over
 * its package) and the projections missed the private cost entirely.
 *
 * FUNDED-SHARE ALLOCATION: which selected services are "the funded ones" is the participant's and
 * provider's choice, not something this model can know. The split is therefore PRO-RATA: the
 * package funds budget/total of the plan, and the contribution applies to that share of each
 * category. Simple, explainable, and neutral between categories.
 */

export interface ServiceQuantity {
    quantity?: number | string;
    frequency?: string;
}

export interface ContributionRatesShape {
    independence?: number;
    everydayLiving?: number;
}

export interface ServiceCatalogCategory {
    label: string;
    services: Array<{ id: string; medianPrice: number }>;
}

export interface PersonServiceCosts {
    totalClinical: number;
    totalIndependence: number;
    totalEveryday: number;
    total: number;
    contribution: { clinical: number; independence: number; everyday: number; total: number };
}

export function convertToMonthly(quantity: number | string | undefined, freqType: string | undefined): number {
    const numericQuantity = Number(quantity);
    if (!quantity || numericQuantity <= 0) return 0;
    switch (freqType) {
        case 'daily':
            return numericQuantity * 30; // Approximate monthly from daily
        case 'weekly':
            return numericQuantity * 4; // Approximate monthly from weekly
        case 'monthly':
            return numericQuantity;
        default:
            return numericQuantity;
    }
}

/** Annual service costs and contribution for ONE person's selections — the single computation the
 *  per-tab and combined views both build on. */
export function computeServiceCostsForPerson(
    servicesMap: Record<string, ServiceQuantity | undefined>,
    rates: ContributionRatesShape,
    catalog: Record<string, ServiceCatalogCategory>,
): PersonServiceCosts {
    let totalClinical = 0;
    let totalIndependence = 0;
    let totalEveryday = 0;

    Object.entries(servicesMap || {}).forEach(([serviceId, data]) => {
        if (!data || !data.quantity || Number(data.quantity) <= 0) return;
        const monthlyQuantity = convertToMonthly(data.quantity, data.frequency || 'monthly');
        Object.values(catalog).forEach((category) => {
            const service = category.services.find((s) => s.id === serviceId);
            if (service) {
                const cost = service.medianPrice * monthlyQuantity * 12; // annual
                if (category.label === 'Clinical Care') totalClinical += cost;
                else if (category.label === 'Independence Services') totalIndependence += cost;
                else if (category.label === 'Everyday Living Services') totalEveryday += cost;
            }
        });
    });

    const independenceContribution = totalIndependence * (rates.independence || 0);
    const everydayContribution = totalEveryday * (rates.everydayLiving || 0);
    return {
        totalClinical,
        totalIndependence,
        totalEveryday,
        total: totalClinical + totalIndependence + totalEveryday,
        contribution: {
            clinical: 0, // Clinical is always 0% contribution
            independence: independenceContribution,
            everyday: everydayContribution,
            total: independenceContribution + everydayContribution,
        },
    };
}

export interface OutOfPocketSplit {
    /** Contribution payable on the funded (within-package) share of the plan. */
    fundedContribution: number;
    /** Services above the package, bought privately at full price. */
    privateExcess: number;
    /** What actually leaves the household each year: fundedContribution + privateExcess. */
    total: number;
    /** Share of the plan the package funds (0..1). */
    fundedShare: number;
}

/** Split one person's plan against THEIR package (budgets are per person and not transferable). */
export function splitOutOfPocket(
    costs: { total: number; contribution: { total: number } },
    budget: number,
): OutOfPocketSplit {
    const safeBudget = Math.max(0, budget || 0);
    const privateExcess = Math.max(0, costs.total - safeBudget);
    const fundedShare = costs.total > 0 ? Math.min(1, safeBudget / costs.total) : 0;
    const fundedContribution = costs.contribution.total * fundedShare;
    return {
        fundedContribution,
        privateExcess,
        total: fundedContribution + privateExcess,
        fundedShare,
    };
}
